/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */

import { RouteHelper } from "helpers/routeHelper";
import { TILE_OUT_OF_VIEW_WIDTH } from "@nf/constants";
import { isServerSideRender } from "@nf/helpers";
import React, { useRef, useState } from "react";
import dynamic from "next/dynamic";
import cx from "classnames";

import LinkWithLocale from "components/LinkWithLocale";
import GenreAnimatedIcon from "components/GenreAnimatedIcon";
import { getItemBounds } from "../../../../utils";
import { type IListComponentItemProps } from "../../models";

import styles from "./ListComponentGenreItemFrame.module.scss";

const ListComponentGenreItemModal = dynamic(
  () => import("../ListComponentGenreItemModal")
);

export type ListComponentGenreItemFrameProps = IListComponentItemProps;

export const ListComponentGenreItemFrame = ({
  media,
  isPlaceholder = false,
}: ListComponentGenreItemFrameProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<number | null>(null);

  const [shouldRender, setShouldRender] = useState(false);
  const [isOutOfView, setIsOutOfView] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const isDesktopSize = !isServerSideRender && window.innerWidth > 850;

  const itemBounds = getItemBounds(ref.current);

  const handleModalAnimationEnd = () => {
    setShouldRender(false);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const handleMouseEnter = () => {
    timeoutRef.current = window.setTimeout(() => {
      setShouldRender(true);
      setIsHovered(true);

      if (!itemBounds) return;

      const isTileOutOnTheLeft = itemBounds.left < 0;
      const isTileOutOnTheRight =
        itemBounds.right > window.innerWidth + TILE_OUT_OF_VIEW_WIDTH;

      if (isTileOutOnTheLeft || isTileOutOnTheRight) {
        setIsOutOfView(true);
      } else {
        setIsOutOfView(false);
      }
    }, 200);
  };

  if (!media || isPlaceholder) {
    return <div className={cx(styles.item, styles.skeleton)} />;
  }

  return (
    <div
      ref={ref}
      className={cx(styles.item, isOutOfView && styles.isOutOfView)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <LinkWithLocale href={RouteHelper.goToGenre(media.Id)}>
        <a aria-label={media.Title}>
          <div className={styles.content}>
            <div className={styles.icon}>
              <GenreAnimatedIcon genreId={media.Id} autoplay={false} />
            </div>
            <span className={styles.title}>{media.Title}</span>
          </div>
        </a>
      </LinkWithLocale>

      {itemBounds && shouldRender && !isOutOfView && isDesktopSize && (
        <ListComponentGenreItemModal
          media={media}
          open={isHovered}
          bounds={itemBounds}
          onAnimationEnd={handleModalAnimationEnd}
        />
      )}
    </div>
  );
};
